import React, { useState, useEffect, useCallback } from 'react'
import { Card } from './ui/card'
import { Pill } from './ui/pill'
import { Input } from './ui/input'
import { Switch } from './ui/switch'
import { 
  Bluetooth, 
  Search, 
  Plus, 
  Settings, 
  Battery, 
  CircuitBoard,
  Wifi,
  WifiOff,
  MoreHorizontal,
  Trash2,
  Edit3,
  Clock,
  Database,
  ChevronDown,
  AlertTriangle,
  FlaskConical
} from 'lucide-react'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from './ui/dropdown-menu'
import { LoadingState } from './ui/LoadingState'
import { EmptyState } from './ui/EmptyState'
import { PageShell, PageHeader } from './ui/PageShell'


import { useUIStore } from '../stores/uiStore'
import { useSystemStatus, useDeviceStatus, useStreamingStatus, useSystemActions, useScanStatus } from '../stores/systemStore'
import { 
  useRegisteredDevices, 
  useDeviceList, 
  useConnectedDevice, 
  useDeviceMonitoring, 
  useBatteryInfo,
  useSamplingRates,
  useDeviceActions,
  useDeviceStore 
} from '../stores/deviceStore'
import { DeviceDetailModal } from './DeviceManager/DeviceDetailModal'
import { DeviceStorageSettingsModal } from './DeviceManager/DeviceStorageSettingsModal'
import { bluetoothService } from '../utils/bluetoothService'
import { Capacitor } from '@capacitor/core'
import BrowserCompatibilityCheck from './PWA/BrowserCompatibilityCheck'
import MobileStorageGuide from './PWA/MobileStorageGuide'
import { createLogger } from "../utils/logger";
import { useTranslation } from "../locales";
import { useLanguageStore } from "../stores/languageStore";


const logger = createLogger("DeviceMgr");
export function DeviceManager() {
  const { currentLanguage } = useLanguageStore();
  const t = useTranslation(currentLanguage);
  const tDm = t.deviceManager;
  // 브라우저 호환성 확인
  const [browserInfo, setBrowserInfo] = useState<{
    isSupported: boolean;
    isIOS: boolean;
    isAndroid: boolean;
    isCapacitor: boolean;
    browserName: string;
    platform: string;
  } | null>(null)
  const { addNotification } = useUIStore()
  
  // 새로운 스토어 구조 사용
  const systemStatus = useSystemStatus()
  const deviceStatus = useDeviceStatus()
  const streamingStatus = useStreamingStatus()
  const scanStatus = useScanStatus()
  const systemActions = useSystemActions()
  
  // DeviceStore Hook들 사용
  const registeredDevices = useRegisteredDevices()
  const availableDevices = useDeviceList()
  const connectedDevice = useConnectedDevice() // 연결된 디바이스 정보
  const deviceMonitoring = useDeviceMonitoring()
  const batteryInfo = useBatteryInfo()
  const samplingRates = useSamplingRates()
  const deviceActions = useDeviceActions()
  
  // 모달 상태 관리
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [selectedDevice, setSelectedDevice] = useState<any>(null)
  const [deviceDetailModalOpen, setDeviceDetailModalOpen] = useState(false)
  const [deviceType, setDeviceType] = useState<'registered' | 'connected'>('registered')
  
  // 등록 확인 모달 상태
  const [registrationModalOpen, setRegistrationModalOpen] = useState(false)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [deviceToRegister, setDeviceToRegister] = useState<any>(null)
  const [deviceNickname, setDeviceNickname] = useState('')
  
  // 디바이스 이름 변경 모달 상태
  const [renameModalOpen, setRenameModalOpen] = useState(false)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [deviceToRename, setDeviceToRename] = useState<any>(null)
  const [newDeviceNickname, setNewDeviceNickname] = useState('')
  
  // 디바이스 저장 설정 모달 상태
  const [storageSettingsModalOpen, setStorageSettingsModalOpen] = useState(false)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [deviceToConfigureStorage, setDeviceToConfigureStorage] = useState<any>(null)
  
  // 자동 등록 상태
  const [autoRegistrationEnabled, setAutoRegistrationEnabled] = useState(() => {
    const saved = localStorage.getItem('autoRegistrationEnabled');
    return saved ? JSON.parse(saved) : true;
  })

  // 자동 연결 상태
  const [autoConnectionEnabled, setAutoConnectionEnabled] = useState(() => {
    const saved = localStorage.getItem('autoConnectionEnabled');
    return saved ? JSON.parse(saved) : true;
  })
  
  // 필터링된 디바이스 목록 (검색 기능 제거)
  const filteredRegisteredDevices = registeredDevices
  const filteredAvailableDevices = availableDevices

  // 시스템 초기화 함수를 useCallback으로 메모이제이션
  const initializeSystem = useCallback(async () => {
    // 이미 초기화 중이거나 완료된 경우 중복 실행 방지
    if (systemStatus.systemStatus === 'initializing' || systemStatus.isInitialized) {
      return;
    }

    try {
      logger.debug('Initializing system...');
      await systemActions.initializeSystem();
      logger.debug('System initialized successfully');
    } catch (error) {
      logger.error('System initialization failed:', error);
      addNotification({
        type: 'error',
        title: tDm.systemInitFailed,
        message: error instanceof Error ? error.message : tDm.systemInitFailedMsg
      });
    }
  }, [systemStatus.systemStatus, systemStatus.isInitialized, systemActions.initializeSystem, addNotification]);

  // 컴포넌트 마운트 시 시스템 초기화 (한 번만 실행)
  useEffect(() => {
    if (!systemStatus.isInitialized && systemStatus.systemStatus === 'idle') {
      initializeSystem();
    }
  }, [systemStatus.isInitialized, systemStatus.systemStatus, initializeSystem]);

  // 자동 등록 설정 저장
  useEffect(() => {
    localStorage.setItem('autoRegistrationEnabled', JSON.stringify(autoRegistrationEnabled));
  }, [autoRegistrationEnabled])

  // 자동 연결 설정 저장
  useEffect(() => {
    localStorage.setItem('autoConnectionEnabled', JSON.stringify(autoConnectionEnabled));
  }, [autoConnectionEnabled])

  // 자동 연결 상태 추적
  const [autoConnectAttempted, setAutoConnectAttempted] = useState(new Set<string>());
  
  // 연결 로딩 상태 추적
  const [connectingDeviceId, setConnectingDeviceId] = useState<string | null>(null);
  
  // 연결 해제 상태 추적
  const [isDisconnecting, setIsDisconnecting] = useState(false);

  // 자동 연결 기능: availableDevices에 새 디바이스가 추가되면 자동으로 연결
  useEffect(() => {
    const autoConnectDevices = async () => {
      // 자동 연결 조건 체크
      if (!autoConnectionEnabled || 
          deviceStatus.isConnected || 
          systemStatus.systemStatus === 'initializing' ||
          systemStatus.systemStatus === 'error' ||
          availableDevices.length === 0 ||
          connectingDeviceId !== null) {
        return;
      }

      // 새로운 디바이스만 자동 연결 시도
      const newDevices = availableDevices.filter(device => 
        !autoConnectAttempted.has(device.id)
      );

      if (newDevices.length === 0) {
        return;
      }

      // 첫 번째 새 디바이스에만 연결 시도
      const deviceToConnect = newDevices[0];
      
      try {
        logger.debug('Auto-connecting to device:', deviceToConnect.name);
        setAutoConnectAttempted(prev => new Set(prev).add(deviceToConnect.id));
        await handleConnect(deviceToConnect.id);
      } catch (error) {
        logger.error('Auto-connect failed for device:', deviceToConnect.name, error);
        // 연결 실패한 디바이스는 자동 연결 시도 목록에서 제거하지 않음
        // 사용자가 수동으로 다시 시도할 수 있도록 함
      }
    };

    // 디바이스 목록이 변경되고 자동 연결이 활성화된 경우에만 실행
    if (availableDevices.length > 0 && autoConnectionEnabled) {
      // 약간의 지연을 두어 연속적인 호출 방지
      const timeoutId = setTimeout(autoConnectDevices, 100);
      return () => clearTimeout(timeoutId);
    }
  }, [availableDevices, deviceStatus.isConnected, systemStatus.systemStatus, autoConnectionEnabled, connectingDeviceId]);

  // 연결이 해제되면 자동 연결 시도 기록 초기화
  useEffect(() => {
    if (!deviceStatus.isConnected) {
      setAutoConnectAttempted(new Set());
    }
  }, [deviceStatus.isConnected])

  // 브라우저 호환성 확인
  useEffect(() => {
    const detectBrowser = () => {
      const userAgent = navigator.userAgent;
      const isIOS = /iPad|iPhone|iPod/.test(userAgent);
      const isAndroid = /Android/.test(userAgent);

      // Capacitor 네이티브 환경 감지 (CoreBluetooth/Android BLE가 실제로 사용 가능한지)
      const isCapacitor = Capacitor.isNativePlatform();

      if (isCapacitor) {
        const platform = isIOS ? 'iOS' : isAndroid ? 'Android' : 'Native';
        return {
          isSupported: true,
          isIOS,
          isAndroid,
          isCapacitor: true,
          browserName: 'LINK BAND SDK',
          platform,
        };
      }

      const hasWebBluetooth = 'bluetooth' in navigator;
      const isSecureContext = window.isSecureContext;

      let browserName = 'Unknown';
      if (userAgent.includes('Chrome')) browserName = 'Chrome';
      else if (userAgent.includes('Safari')) browserName = 'Safari';
      else if (userAgent.includes('Firefox')) browserName = 'Firefox';
      else if (userAgent.includes('Edge')) browserName = 'Edge';

      const platform = isIOS ? 'iOS' : isAndroid ? 'Android' : 'Desktop';
      const isSupported = hasWebBluetooth && isSecureContext && !isIOS;

      return {
        isSupported,
        isIOS,
        isAndroid,
        isCapacitor: false,
        browserName,
        platform
      };
    };

    setBrowserInfo(detectBrowser());
  }, []);

  // 연결된 디바이스의 실시간 정보 (BluetoothService에서 직접 조회)
  const [realtimeDeviceInfo, setRealtimeDeviceInfo] = useState<{
    batteryLevel: number;
    connectionDuration: number;
    connectionStartTime: number;
    samplingRates: { eeg: number; ppg: number; acc: number };
    batteryPrediction: {
      mode: 'charging' | 'discharging' | 'unknown';
      timeRemainingFormatted: string;
      ratePerMinute: number;
    };
  } | null>(null);
  
  useEffect(() => {
    let interval: NodeJS.Timeout;
    
    if (deviceStatus.isConnected && bluetoothService.isConnected()) {
      interval = setInterval(async () => {
        try {
          const batteryLevel = await bluetoothService.getBatteryLevel();
          const batteryPrediction = bluetoothService.getBatteryPrediction();
          
          setRealtimeDeviceInfo({
            batteryLevel,
            connectionDuration: bluetoothService.getConnectionDuration(),
            connectionStartTime: bluetoothService.getConnectionStartTime(),
            samplingRates: bluetoothService.getCurrentSamplingRates(),
            batteryPrediction: {
              mode: batteryPrediction.mode,
              timeRemainingFormatted: batteryPrediction.timeRemainingFormatted,
              ratePerMinute: batteryPrediction.ratePerMinute
            }
          });
        } catch (error) {
          logger.error('Failed to get realtime device info:', error);
        }
      }, 1000); // 1초마다 업데이트
    } else {
      setRealtimeDeviceInfo(null);
    }
    
    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [deviceStatus.isConnected]);

  const handleScan = async () => {
    // Web Bluetooth(navigator.bluetooth)은 Chrome/Edge 등 데스크톱 브라우저에서만 제공.
    // Capacitor 네이티브 앱(iOS/Android)은 BluetoothProvider가 CoreBluetooth/Android BLE로
    // 직접 스캔하므로 이 체크를 건너뛴다.
    if (!Capacitor.isNativePlatform() && !navigator.bluetooth) {
      if (browserInfo?.isIOS) {
        addNotification({
          type: 'warning',
          title: tDm.iosBrowserGuide,
          message: tDm.iosBrowserGuideMsg,
          autoHide: false
        });
      } else {
        addNotification({
          type: 'error',
          title: tDm.bluetoothNotSupported,
          message: tDm.bluetoothNotSupportedMsg,
          autoHide: false
        });
      }
      return;
    }

    try {
      // SystemControlService를 사용하여 디바이스 스캔
      await systemActions.scanDevices();

      addNotification({
        type: 'success',
        title: tDm.scanComplete,
        message: `${scanStatus.availableDevices.length} ${tDm.scanCompleteMsg}`
      });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : tDm.scanFailedMsg;

      // 사용자가 취소한 경우 알림을 표시하지 않음
      if (!errorMessage.includes('cancelled') && !errorMessage.includes('취소')) {
        addNotification({
          type: 'error',
          title: tDm.scanFailed,
          message: errorMessage
        });
      }
    }
  };

  // 기기 없이 가상 LINK BAND 로 체험 (App Store 심사·신규 사용자용)
  const handleTryDemo = async () => {
    try {
      await bluetoothService.enableDemoMode();
      await systemActions.scanDevices();
    } catch (error) {
      addNotification({
        type: 'error',
        title: tDm.demoStartFailed,
        message: error instanceof Error ? error.message : String(error)
      });
    }
  };

  const handleConnect = async (deviceId: string) => {
    // 이미 연결 중인 경우 중복 연결 방지
    if (connectingDeviceId === deviceId) {
      logger.debug('Connection already in progress for device:', deviceId);
      return;
    }

    // 이미 연결된 디바이스인 경우 연결 시도 방지
    if (deviceStatus.isConnected && deviceStatus.currentDeviceId === deviceId) {
      logger.debug('Device already connected:', deviceId);
      return;
    }

    try {
      // 연결 시작 시 로딩 상태 설정
      setConnectingDeviceId(deviceId);
      
      // 시스템이 초기화되지 않은 경우 대기
      if (systemStatus.systemStatus === 'initializing') {
        logger.debug('Waiting for system initialization...');
        // 최대 5초 대기
        let waitTime = 0;
        while (systemStatus.systemStatus === 'initializing' && waitTime < 5000) {
          await new Promise(resolve => setTimeout(resolve, 100));
          waitTime += 100;
        }
        
        if (systemStatus.systemStatus === 'initializing') {
          throw new Error('System initialization timeout');
        }
      }

      logger.debug('Connecting to device:', deviceId);
      await systemActions.connectDevice(deviceId);
      
      // 연결 성공 후 등록되지 않은 디바이스인지 확인
      const isRegistered = deviceActions.isDeviceRegistered(deviceId);
      const connectedDevice = availableDevices.find(device => device.id === deviceId);
      
      if (!isRegistered && connectedDevice) {
        if (autoRegistrationEnabled) {
          // 자동 등록이 활성화된 경우 바로 등록
          try {
            deviceActions.registerDevice(connectedDevice, connectedDevice.name);
            addNotification({
              type: 'success',
              title: tDm.deviceAutoRegistered,
              message: `${connectedDevice.name} ${tDm.deviceAutoRegisteredMsg}`
            });
          } catch (error) {
            logger.error('Auto-registration failed:', error);
            addNotification({
              type: 'error',
              title: tDm.autoRegistrationFailed,
              message: error instanceof Error ? error.message : tDm.autoRegistrationFailedMsg
            });
          }
        } else {
          // 자동 등록이 비활성화된 경우 등록 모달 표시
          setDeviceToRegister(connectedDevice);
          setDeviceNickname(connectedDevice.name || ''); // 기본값으로 디바이스 이름 설정
          setRegistrationModalOpen(true);
        }
      }
      
      addNotification({
        type: 'success',
        title: tDm.deviceConnectedTitle,
        message: `${tDm.deviceConnectedMsg}: ${connectedDevice?.name || ''}`
      });
      
      logger.debug('Device connected successfully:', deviceId);
      
    } catch (error) {
      logger.error('Connection failed:', error);
      const errorMessage = error instanceof Error ? error.message : tDm.connectionFailedMsg;

      // 사용자가 취소한 경우 알림을 표시하지 않음
      if (!errorMessage.includes('cancelled') && !errorMessage.includes('취소')) {
        addNotification({
          type: 'error',
          title: tDm.connectionFailed,
          // 웹 환경: OS 블루투스에 페어링된 밴드는 Chrome GATT 연결을 막는 경우가 많아 해결 안내를 덧붙인다
          message: Capacitor.isNativePlatform()
            ? errorMessage
            : `${errorMessage} — ${tDm.connectionFailedPairedHint}`
        });
      }
      
      // 연결 실패 시 자동 연결 시도 목록에서 제거 (다시 시도할 수 있도록)
      setAutoConnectAttempted(prev => {
        const newSet = new Set(prev);
        newSet.delete(deviceId);
        return newSet;
      });
      
      throw error; // 자동 연결 로직에서 에러를 처리할 수 있도록 다시 throw
    } finally {
      // 연결 시도 완료 후 로딩 상태 해제
      setConnectingDeviceId(null);
    }
  };

  const handleDisconnect = async () => {
    setIsDisconnecting(true);
    try {
      await systemActions.disconnectDevice();
      
      addNotification({
        type: 'info',
        title: tDm.deviceDisconnected,
        message: tDm.deviceDisconnectedMsg
      });
    } catch (error) {
      addNotification({
        type: 'error',
        title: tDm.disconnectionFailed,
        message: error instanceof Error ? error.message : tDm.disconnectionFailedMsg
      });
    } finally {
      setIsDisconnecting(false);
    }
  };

  // 강제 연결 해제 핸들러
  const handleForceDisconnect = async () => {
    setIsDisconnecting(true);
    try {
      logger.debug('🔧 강제 연결 해제 시도...');
      
      // 1. 시스템 상태 강제 초기화
      await systemActions.disconnectDevice().catch(e => {
        logger.warn('일반 연결 해제 실패, 강제 진행:', e);
      });
      
      // 2. Bluetooth 캐시 강제 정리
      bluetoothService.clearDeviceCache();
      
      // 3. 추가 대기 시간
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      addNotification({
        type: 'success',
        title: tDm.forceDisconnectComplete,
        message: tDm.forceDisconnectCompleteMsg
      });
    } catch (error) {
      logger.error('강제 연결 해제 실패:', error);
      addNotification({
        type: 'error',
        title: tDm.forceDisconnectFailed,
        message: tDm.forceDisconnectFailedMsg
      });
    } finally {
      setIsDisconnecting(false);
    }
  };

  // 등록 모달에서 등록하기 선택
  const handleConfirmRegistration = () => {
    if (!deviceToRegister) return;
    
    try {
      const nickname = deviceNickname.trim() !== deviceToRegister.name ? deviceNickname.trim() : undefined;
      deviceActions.registerDevice(deviceToRegister, nickname);
      
      addNotification({
        type: 'success',
        title: tDm.deviceRegistered,
        message: `${deviceToRegister.name} ${tDm.deviceRegisteredMsg}`
      });
    } catch (error) {
      addNotification({
        type: 'error',
        title: tDm.registrationFailed,
        message: error instanceof Error ? error.message : tDm.registrationFailedMsg
      });
    } finally {
      setRegistrationModalOpen(false);
      setDeviceToRegister(null);
      setDeviceNickname('');
    }
  };

  // 등록 모달에서 건너뛰기 선택
  const handleSkipRegistration = () => {
    setRegistrationModalOpen(false);
    setDeviceToRegister(null);
    setDeviceNickname('');
  };

  // 디바이스 해지 기능
  const handleUnregisterDevice = (deviceId: string) => {
    try {
      deviceActions.unregisterDevice(deviceId);
      addNotification({
        type: 'info',
        title: tDm.deviceUnregistered,
        message: tDm.deviceUnregisteredMsg
      });
    } catch (error) {
      addNotification({
        type: 'error',
        title: tDm.unregistrationFailed,
        message: error instanceof Error ? error.message : tDm.unregistrationFailedMsg
      });
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleViewDeviceDetails = (device: any, type: 'registered' | 'connected') => {
    setSelectedDevice(device);
    setDeviceType(type);
    setDeviceDetailModalOpen(true);
  };

  // 디바이스 이름 변경 모달 열기
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleRenameDevice = (device: any) => {
    setDeviceToRename(device);
    setNewDeviceNickname(device.nickname || device.name || '');
    setRenameModalOpen(true);
  };

  // 디바이스 이름 변경 확인
  const handleConfirmRename = () => {
    if (!deviceToRename) return;
    
    try {
      const trimmedNickname = newDeviceNickname.trim();
      
      // 20자 제한 검증
      if (trimmedNickname.length > 20) {
        addNotification({
          type: 'error',
          title: tDm.renameFailed,
          message: tDm.nicknameTooLong
        });
        return;
      }

      // 빈 문자열이거나 원래 이름과 같으면 기본 이름 사용
      const finalNickname = trimmedNickname === deviceToRename.name ? '' : trimmedNickname;

      deviceActions.renameDevice(deviceToRename.id, finalNickname);

      addNotification({
        type: 'success',
        title: tDm.deviceRenamed,
        message: `${tDm.deviceRenamedMsg} "${finalNickname || deviceToRename.name}"`
      });
    } catch (error) {
      addNotification({
        type: 'error',
        title: tDm.renameFailed,
        message: error instanceof Error ? error.message : tDm.renameFailedMsg
      });
    } finally {
      setRenameModalOpen(false);
      setDeviceToRename(null);
      setNewDeviceNickname('');
    }
  };

  // 디바이스 이름 변경 취소
  const handleCancelRename = () => {
    setRenameModalOpen(false);
    setDeviceToRename(null);
    setNewDeviceNickname('');
  }
  
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleConfigureStorage = (device: any) => {
    setDeviceToConfigureStorage(device)
    setStorageSettingsModalOpen(true)
  };

  // 브라우저 호환성 체크 - 지원되지 않는 브라우저인 경우 안내 표시
  if (browserInfo && !browserInfo.isSupported) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-4xl mx-auto">
          <BrowserCompatibilityCheck />
        </div>
      </div>
    );
  }

  return (
    <PageShell>
      <MobileStorageGuide />
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <PageHeader title={tDm.pageHeading} subtitle={tDm.pageSubheading} />
        <div className="flex items-center gap-3 w-full sm:w-auto">
            <Pill
              variant="primary"
              onClick={handleScan}
              aria-label={tDm.scan}
              disabled={scanStatus.isScanning || deviceStatus.isConnected}
              className="w-full sm:w-auto justify-center"
            >
              {scanStatus.isScanning ? (
                <>
                  <LoadingState variant="inline" className="w-4 h-4 mr-2 text-white" />
                  {tDm.scanning}
                </>
              ) : (
                <>
                  <Search className="w-4 h-4 mr-2" strokeWidth={1.5} />
                  {tDm.scanDevices}
                </>
              )}
            </Pill>
            <Pill
              variant="secondary"
              onClick={handleTryDemo}
              aria-label={tDm.demoTry}
              title={tDm.demoTryHint}
              disabled={scanStatus.isScanning || deviceStatus.isConnected}
              className="w-full sm:w-auto justify-center"
            >
              <FlaskConical className="w-4 h-4 mr-2" strokeWidth={1.5} />
              {tDm.demoTry}
            </Pill>
          </div>
        </div>

        {/* 브라우저별 안내 */}
        {!deviceStatus.isConnected && browserInfo && (
          <div className={`${
            (browserInfo.isIOS && !browserInfo.isCapacitor) ? 'bg-indigo-500/10 bg-indigo-500/10 border-indigo-500/20 border-indigo-500/20' :
            browserInfo.isAndroid ? 'bg-green-500/10 bg-green-500/10 border-green-500/20' :
            'bg-purple-500/10 border-purple-500/20'
          } border rounded-lg p-4`}>
            <div className="flex items-start space-x-3">
              <div className={`${
                (browserInfo.isIOS && !browserInfo.isCapacitor) ? 'text-indigo-400 text-indigo-400' :
                browserInfo.isAndroid ? 'text-green-400' :
                'text-purple-400'
              }`}>
                <Bluetooth className="w-5 h-5" />
              </div>
              <div>
                <h3 className={`font-medium mb-2 ${
                  (browserInfo.isIOS && !browserInfo.isCapacitor) ? 'text-indigo-200 text-indigo-300' :
                  browserInfo.isAndroid ? 'text-green-900 text-green-400' :
                  'text-purple-900 text-purple-300'
                }`}>
                  {(browserInfo.isIOS && !browserInfo.isCapacitor) ? tDm.iosUserGuide :
                   browserInfo.isAndroid ? tDm.androidUserGuide :
                   tDm.desktopUserGuide}
                </h3>

                {(browserInfo.isIOS && !browserInfo.isCapacitor) ? (
                  <div className="space-y-2">
                    <p className="text-sm text-indigo-300 text-indigo-300 font-medium">
                      {tDm.iosSafariNotSupported}
                    </p>
                    <ul className="text-sm text-indigo-300 text-indigo-300 space-y-1">
                      <li>• <strong>Bluefy</strong> - {tDm.iosRecommendedBrowser}</li>
                      <li>• <strong>WebBLE</strong> - {tDm.iosAlternativeBrowser}</li>
                    </ul>
                    <p className="text-sm text-indigo-400 text-indigo-300">
                      {tDm.iosDownloadInstruction} <code className="bg-indigo-500/20 bg-indigo-500/20 px-1 rounded">sdk.linkband.store</code>
                    </p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <p className="text-sm font-medium mb-2">{tDm.devicePrepTitle}</p>
                    <ul className={`text-sm space-y-1 ${
                      browserInfo.isAndroid ? 'text-green-400 text-green-400' :
                      'text-purple-800 text-purple-300'
                    }`}>
                      <li>• {tDm.devicePrepPower}</li>
                      <li>• {tDm.devicePrepPairing}</li>
                      <li>• {tDm.devicePrepClose}</li>
                      <li>• {browserInfo.isAndroid ? tDm.devicePrepBrowserAndroid : tDm.devicePrepBrowserDesktop}</li>
                    </ul>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* 연결 중 상태 표시 */}
        {systemStatus.systemStatus === 'initializing' && (
          <div className="bg-yellow-500/10 bg-yellow-500/10 border border-yellow-500/20 rounded-lg p-4">
            <div className="flex items-center space-x-3">
              <div className="animate-spin w-5 h-5 border-2 border-yellow-600 border-t-transparent rounded-full"></div>
              <div>
                <h3 className="font-medium text-yellow-900 text-yellow-400">
                  {tDm.deviceConnecting}
                </h3>
                <p className="text-sm text-yellow-400 text-yellow-400">
                  {tDm.deviceConnectingDesc}
                </p>
              </div>
            </div>
          </div>
        )}



        {/* 연결 오류 상태 표시 */}
        {systemStatus.systemError && (
          <div className="bg-red-50 bg-red-500/10 border border-red-200 border-red-500/20 rounded-lg p-4">
            <div className="flex items-start space-x-3">
              <div className="text-red-600 text-red-400">
                <WifiOff className="w-5 h-5" />
              </div>
              <div>
                <h3 className="font-medium text-red-900 text-red-400">
                  {tDm.connectionFailedTitle}
                </h3>
                <p className="text-sm text-red-800 text-red-400 mb-2">
                  {systemStatus.systemError || tDm.unknownError}
                </p>
                <div className="text-sm text-red-700 text-red-400">
                  <p className="font-medium mb-1">{tDm.solution}</p>
                  <ul className="space-y-1">
                    <li>• {tDm.solutionPower}</li>
                    <li>• {tDm.solutionPairing}</li>
                    <li>• {tDm.solutionRefresh}</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Connected Device Details */}
        {deviceStatus.isConnected && realtimeDeviceInfo && (
          <div className="bg-gradient-to-r from-green-50 to-blue-50 from-green-950/20 to-indigo-900/20 border border-green-500/20 rounded-lg p-3 md:p-6">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-4">
              <div className="flex flex-col md:flex-row md:items-center gap-3 md:space-x-3">
                <div className="w-12 h-12 bg-green-100 bg-green-500/20 rounded-lg flex items-center justify-center">
                  <CircuitBoard className="w-6 h-6 text-green-600 text-green-400" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-green-900 text-green-400">
                    {bluetoothService.getDeviceName() || 'LINK BAND'}
                  </h3>
                  <p className="text-sm text-green-700 text-green-400">
                    {tDm.realtimeReceiving}
                  </p>
                </div>
              </div>
              <div className="flex flex-col md:flex-row md:items-center gap-3 w-full md:w-auto">
                <Pill size="sm" variant="secondary" className="bg-green-500/10 text-white w-full md:w-auto justify-center">
                  <div className="w-2 h-2 bg-card rounded-full mr-2 animate-pulse"></div>
                  {tDm.live}
                </Pill>
                <div className="flex items-center gap-2 w-full md:w-auto">
                  <Pill
                    variant="secondary"
                    className="text-red-400 border-red-500/30 flex-1 md:flex-none justify-center"
                    onClick={handleDisconnect}
                    disabled={isDisconnecting}
                  >
                    {isDisconnecting ? (
                      <>
                        <div className="animate-spin w-4 h-4 mr-2 border border-red-600 border-t-transparent rounded-full"></div>
                        {tDm.disconnectingShort}
                      </>
                    ) : (
                      <>
                        <WifiOff className="w-4 h-4 mr-2" strokeWidth={1.5} />
                        {tDm.disconnectShort}
                      </>
                    )}
                  </Pill>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button
                        className="inline-flex items-center justify-center h-8 w-8 rounded-md hover:bg-white/[0.04] text-red-400 disabled:opacity-50"
                        disabled={isDisconnecting}
                      >
                        <ChevronDown className="w-4 h-4" strokeWidth={1.5} />
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem
                        onClick={handleForceDisconnect}
                        className="text-red-600 focus:text-red-600"
                        disabled={isDisconnecting}
                      >
                        <AlertTriangle className="w-4 h-4 mr-2" strokeWidth={1.5} />
                        {tDm.forceDisconnectShort}
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* 배터리 정보 */}
              <div className="bg-card dark:bg-card rounded-lg p-4 border border-green-100 border-green-500/20">
                <div className="flex items-center gap-2 mb-2">
                  <Battery className="w-4 h-4 text-green-600" />
                  <span className="text-sm font-medium text-foreground dark:text-foreground">Battery</span>
                </div>
                <div className="lb-display-lg text-foreground">
                  {realtimeDeviceInfo?.batteryLevel || 0}%
                </div>
                <div className="text-sm text-muted-foreground dark:text-muted-foreground">
                  {realtimeDeviceInfo?.batteryPrediction?.timeRemainingFormatted || 'Unknown'}
                </div>
                {realtimeDeviceInfo?.batteryPrediction?.mode !== 'unknown' && (
                  <div className="text-xs text-muted-foreground dark:text-muted-foreground">
                    {realtimeDeviceInfo?.batteryPrediction?.mode === 'charging' ? tDm.chargingLabel : tDm.dischargingLabel}
                    {' '}({realtimeDeviceInfo?.batteryPrediction?.ratePerMinute.toFixed(1)}%/min)
                  </div>
                )}
              </div>
              
              {/* 연결 시간 정보 */}
              <div className="bg-card dark:bg-card rounded-lg p-4 border border-blue-100 border-indigo-500/20">
                <div className="flex items-center gap-2 mb-2">
                  <Clock className="w-4 h-4 text-indigo-400" />
                  <span className="text-sm font-medium text-foreground dark:text-foreground">Connection</span>
                </div>
                <div className="text-lg font-bold text-foreground">
                  {realtimeDeviceInfo?.connectionStartTime ? 
                    new Date(realtimeDeviceInfo.connectionStartTime).toLocaleTimeString() : 'N/A'
                  }
                </div>
                <div className="text-sm text-muted-foreground dark:text-muted-foreground">
                  Duration: {realtimeDeviceInfo ? 
                    (() => {
                      const totalSeconds = Math.floor(realtimeDeviceInfo.connectionDuration / 1000);
                      const hours = Math.floor(totalSeconds / 3600);
                      const minutes = Math.floor((totalSeconds % 3600) / 60);
                      const seconds = totalSeconds % 60;
                      return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
                    })() : '00:00:00'
                  }
                </div>
              </div>
              
              {/* 샘플링 레이트 */}
              <div className="bg-card dark:bg-card rounded-lg p-4 border border-purple-100 border-purple-500/20">
                <div className="flex items-center gap-2 mb-2">
                  <Wifi className="w-4 h-4 text-purple-600" />
                  <span className="text-sm font-medium text-foreground dark:text-foreground">Sampling Rates</span>
                </div>
                <div className="space-y-1 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground dark:text-muted-foreground">EEG:</span>
                    <span className="font-medium text-foreground">{realtimeDeviceInfo?.samplingRates?.eeg || 0}Hz</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground dark:text-muted-foreground">PPG:</span>
                    <span className="font-medium text-foreground">{realtimeDeviceInfo?.samplingRates?.ppg || 0}Hz</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground dark:text-muted-foreground">ACC:</span>
                    <span className="font-medium text-foreground">{realtimeDeviceInfo?.samplingRates?.acc || 0}Hz</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Available Devices */}
        <Card className="p-4 md:p-6 hover:bg-card/90 transition-colors">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-4">
            <h3 className="font-medium">{tDm.availableDevices} ({filteredAvailableDevices.length})</h3>
            <div className="flex flex-col md:flex-row md:items-center gap-3 w-full md:w-auto">
              <div className="flex items-center justify-between md:justify-start gap-3">
                <span className="text-sm font-medium text-muted-foreground dark:text-muted-foreground">{tDm.autoConnectShort}</span>
                <Switch
                  checked={autoConnectionEnabled}
                  onCheckedChange={setAutoConnectionEnabled}
                />
              </div>
              <Pill variant="secondary" size="sm" onClick={handleScan} disabled={scanStatus.isScanning} className="w-full md:w-auto justify-center">
                {scanStatus.isScanning ? (
                  <>
                    <div className="animate-spin w-4 h-4 mr-2 border border-muted-foreground/20 border-t-transparent rounded-full"></div>
                    {tDm.scanningShort}
                  </>
                ) : (
                  <>
                    <Search className="w-4 h-4 mr-2" strokeWidth={1.5} />
                    {tDm.refresh}
                  </>
                )}
              </Pill>
            </div>
          </div>

          {scanStatus.isScanning ? (
            <div className="text-center py-12">
              <div className="animate-spin w-8 h-8 border-2 border-border border-t-indigo-400 rounded-full mx-auto mb-4"></div>
              <p className="text-muted-foreground dark:text-muted-foreground mb-2">{tDm.scanningForDevices}</p>
              <p className="text-sm text-muted-foreground dark:text-muted-foreground">
                {tDm.pairingHint}
              </p>
            </div>
          ) : filteredAvailableDevices.length > 0 ? (
            <div className="space-y-3">
              {filteredAvailableDevices.map((device) => (
                <Card key={device.id} className="p-4 hover:bg-card/90 transition-colors">
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-muted dark:bg-muted rounded-lg flex items-center justify-center shrink-0">
                        <CircuitBoard className="w-6 h-6 text-muted-foreground dark:text-foreground" strokeWidth={1.5} />
                      </div>
                      <div className="min-w-0">
                        <h4 className="font-medium text-foreground truncate">{device.name}</h4>
                        <p className="text-sm text-muted-foreground dark:text-muted-foreground truncate">{device.id}</p>

                      </div>
                    </div>

                    <div className="flex flex-wrap items-center gap-2 w-full md:w-auto">
                      {/* 연결된 디바이스인지 확인하여 Badge 표시 */}
                      {deviceStatus.isConnected && deviceStatus.currentDeviceId === device.id ? (
                        <Pill size="sm" variant="secondary" className="bg-green-500/10 text-white">{tDm.connectedStatus}</Pill>
                      ) : (
                        <Pill size="sm" variant="secondary" className="bg-muted/50 text-foreground">{tDm.available}</Pill>
                      )}


                      {/* 자동 연결 상태 표시 */}
                      {deviceStatus.isConnected && deviceStatus.currentDeviceId === device.id ? (
                        <div className="flex items-center gap-2">
                          <div className="w-2 h-2 bg-green-500/10 rounded-full animate-pulse"></div>
                          <span className="text-sm font-medium text-green-600">{tDm.connectedStatus}</span>
                        </div>
                      ) : connectingDeviceId === device.id ? (
                        <div className="flex items-center gap-2">
                          <div className="animate-spin w-4 h-4 border-2 border-indigo-500/30 border-t-transparent rounded-full"></div>
                          <span className="text-sm font-medium text-indigo-400">{tDm.connecting}</span>
                        </div>
                      ) : autoConnectionEnabled ? (
                        <div className="flex items-center gap-2">
                          <div className="w-2 h-2 bg-indigo-500 rounded-full animate-pulse"></div>
                          <span className="text-sm font-medium text-indigo-400">{tDm.autoConnectWaiting}</span>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2">
                          <Pill
                            size="sm"
                            variant="primary"
                            onClick={() => handleConnect(device.id)}
                            disabled={connectingDeviceId === device.id}
                          >
                            {connectingDeviceId === device.id ? (
                              <>
                                <div className="animate-spin w-3 h-3 mr-2 border border-white border-t-transparent rounded-full"></div>
                                {tDm.connecting}
                              </>
                            ) : (
                              tDm.connect
                            )}
                          </Pill>
                        </div>
                      )}
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          ) : (
            <EmptyState
              icon={<Bluetooth className="w-8 h-8 text-muted-foreground" strokeWidth={1.5} />}
              title={tDm.noAvailableDevices}
              description={tDm.noAvailableDevicesDesc}
              action={{ label: scanStatus.isScanning ? tDm.scanningShort : tDm.scanForDevices, onClick: handleScan }}
            />
          )}
        </Card>

        {/* Registered Devices */}
        <Card className="p-4 md:p-6 hover:bg-card/90 transition-colors">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-4">
            <h3 className="font-medium">{tDm.registeredDevices} ({filteredRegisteredDevices.length})</h3>
            <div className="flex flex-col md:flex-row md:items-center gap-3 w-full md:w-auto">
              <div className="flex items-center justify-between md:justify-start gap-3">
                <span className="text-sm font-medium text-muted-foreground dark:text-muted-foreground">{tDm.autoRegisterShort}</span>
                <Switch
                  checked={autoRegistrationEnabled}
                  onCheckedChange={setAutoRegistrationEnabled}
                />
              </div>
              <Pill size="sm" variant="secondary" className="text-indigo-400 border-indigo-500/30 bg-indigo-500/10 justify-center md:justify-start">
                <div className="w-2 h-2 bg-indigo-500 rounded-full mr-2"></div>
                {tDm.registered}
              </Pill>
            </div>
          </div>

          {filteredRegisteredDevices.length > 0 ? (
            <div className="space-y-3">
              {filteredRegisteredDevices.map((device) => (
                <Card key={device.id} className="p-4 hover:bg-card/90 transition-colors">
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-muted dark:bg-muted rounded-lg flex items-center justify-center shrink-0">
                        <CircuitBoard className="w-6 h-6 text-foreground dark:text-foreground" strokeWidth={1.5} />
                      </div>
                      <div className="min-w-0">
                        <h4 className="font-medium text-foreground truncate">{device.name}</h4>
                        <p className="text-sm text-muted-foreground dark:text-muted-foreground">{device.nickname || device.id}</p>
                        <div className="flex items-center gap-4 mt-2">
                          <div className="flex items-center gap-1">
                            <CircuitBoard className="w-3 h-3 text-muted-foreground dark:text-muted-foreground" strokeWidth={1.5} />
                            <span className="text-xs text-muted-foreground dark:text-muted-foreground">{tDm.connectionCount} {device.connectionCount}</span>
                          </div>
                          <div className="flex items-center gap-1">
                            <Clock className="w-3 h-3 text-muted-foreground dark:text-muted-foreground" strokeWidth={1.5} />
                            <span className="text-xs text-muted-foreground dark:text-muted-foreground">
                              {tDm.lastShort}: {device.lastConnectedAt ? new Date(device.lastConnectedAt).toLocaleDateString() : '-'}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex flex-col md:flex-row md:items-center gap-3 md:gap-4 w-full md:w-auto">
                      <div className="text-left md:text-right">
                        {/* 연결된 디바이스인지 확인 */}
                        {deviceStatus.isConnected && deviceStatus.currentDeviceId === device.id ? (
                          <Pill size="sm" variant="secondary" className="mb-2 bg-green-500/10 text-white">{tDm.connectedStatus}</Pill>
                        ) : (
                          <Pill size="sm" variant="secondary" className="mb-2 bg-card text-foreground">{tDm.registered}</Pill>
                        )}
                        <p className="text-xs text-muted-foreground dark:text-muted-foreground">{tDm.registeredOn}: {new Date(device.registeredAt).toLocaleDateString()}</p>
                        <p className="text-xs text-muted-foreground dark:text-muted-foreground truncate">{tDm.idLabel}: {device.id}</p>
                      </div>

                      <div className="flex items-center gap-2 w-full md:w-auto">
                        {/* 연결된 디바이스인지 확인하여 버튼 상태 결정 */}
                        {deviceStatus.isConnected && deviceStatus.currentDeviceId === device.id ? (
                          <Pill
                            variant="secondary"
                            disabled
                          >
                            Connected
                          </Pill>
                        ) : (
                          <Pill
                            variant="secondary"
                            onClick={() => handleConnect(device.id)}
                            disabled={systemStatus.systemStatus === 'initializing' || connectingDeviceId === device.id}
                          >
                            {connectingDeviceId === device.id ? (
                              <>
                                <div className="animate-spin w-4 h-4 mr-2 border-2 border-current border-t-transparent rounded-full"></div>
                                {t.deviceManager.connecting}
                              </>
                            ) : systemStatus.systemStatus === 'initializing' ? (
                              'Connecting...'
                            ) : (
                              'Connect'
                            )}
                          </Pill>
                        )}



                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <button className="inline-flex items-center justify-center h-8 w-8 rounded-md hover:bg-white/[0.04] text-muted-foreground">
                              <MoreHorizontal className="w-4 h-4" strokeWidth={1.5} />
                            </button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent 
                            align="end"
                            className="bg-card/95 dark:bg-card/95 backdrop-blur-md border-border dark:border-border"
                          >
                            <DropdownMenuItem 
                              onClick={() => handleViewDeviceDetails(device, 'registered')}
                              className="text-muted-foreground text-foreground hover:bg-card dark:hover:bg-card focus:bg-card dark:focus:bg-card"
                            >
                              <Settings className="w-4 h-4 mr-2" strokeWidth={1.5} />
                              View Details
                            </DropdownMenuItem>
                            <DropdownMenuItem 
                              onClick={() => handleRenameDevice(device)}
                              className="text-muted-foreground text-foreground hover:bg-card dark:hover:bg-card focus:bg-card dark:focus:bg-card"
                            >
                              <Edit3 className="w-4 h-4 mr-2" strokeWidth={1.5} />
                              Rename
                            </DropdownMenuItem>
                            <DropdownMenuItem 
                              onClick={() => handleConfigureStorage(device)}
                              className="text-muted-foreground text-foreground hover:bg-card dark:hover:bg-card focus:bg-card dark:focus:bg-card"
                            >
                              <Database className="w-4 h-4 mr-2" strokeWidth={1.5} />
                              Storage Settings
                            </DropdownMenuItem>
                            {/* 연결되지 않은 경우에만 Unregister 메뉴 표시 */}
                            {!(deviceStatus.isConnected && deviceStatus.currentDeviceId === device.id) && (
                              <DropdownMenuItem 
                                onClick={() => handleUnregisterDevice(device.id)}
                                className="text-red-400 text-red-400 hover:bg-red-900/20 hover:bg-red-500/20 focus:bg-red-900/20 focus:bg-red-500/20"
                              >
                                <Trash2 className="w-4 h-4 mr-2" strokeWidth={1.5} />
                                Unregister
                              </DropdownMenuItem>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <CircuitBoard className="w-12 h-12 text-muted-foreground dark:text-muted-foreground mx-auto mb-4" strokeWidth={1.5} />
              <p className="text-muted-foreground dark:text-muted-foreground mb-2">No registered devices</p>
              <p className="text-sm text-muted-foreground dark:text-muted-foreground">
                {t.deviceManager.noRegisteredDevicesDesc}
              </p>
            </div>
          )}
        </Card>

        {/* Device Detail Modal */}
        <DeviceDetailModal
          device={selectedDevice}
          deviceType={deviceType}
          isOpen={deviceDetailModalOpen}
          onClose={() => {
            setDeviceDetailModalOpen(false);
            setSelectedDevice(null);
          }}
        />

        {/* Registration Confirmation Modal - Floating over Device Manager */}
        {registrationModalOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center">
            {/* Backdrop with blur */}
            <div 
              className="absolute inset-0 bg-background/20 backdrop-blur-sm"
              onClick={handleSkipRegistration}
            />
            
            {/* Modal Content */}
            <div className="relative bg-card dark:bg-background rounded-lg shadow-xl border border-border dark:border-border w-full max-w-md max-w-[95vw] md:max-w-md mx-2 md:mx-4">
              {/* Header */}
              <div className="flex items-center gap-3 p-6 border-b border-border dark:border-border">
                <div className="w-10 h-10 bg-indigo-500/20 bg-indigo-500/20 rounded-lg flex items-center justify-center">
                  <CircuitBoard className="w-5 h-5 text-indigo-400 text-indigo-400" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-foreground">
                    {t.deviceManager.registerYourDevice}
                  </h3>
                  <p className="text-sm text-muted-foreground dark:text-muted-foreground">
                    {t.deviceManager.registerYourDeviceDesc}
                  </p>
                </div>
              </div>
              
              {/* Body */}
              <div className="p-6 space-y-4">
                <div className="text-center">
                  <p className="text-foreground dark:text-foreground mb-2">
                    <span className="font-medium text-indigo-400 text-indigo-400">
                      {deviceToRegister?.name}
                    </span>{t.deviceManager.registerConfirmText}
                  </p>
                </div>
                
                                 {/* Nickname Input */}
                 <div className="space-y-2">
                   <label htmlFor="device-nickname" className="text-sm font-medium text-foreground dark:text-foreground">
                     {t.deviceManager.nicknameOptional}
                   </label>
                  <Input
                    id="device-nickname"
                    type="text"
                    value={deviceNickname}
                    onChange={(e) => setDeviceNickname(e.target.value)}
                    placeholder={t.deviceManager.nicknamePlaceholder}
                    className="w-full bg-card dark:bg-card text-white text-foreground border-border dark:border-border placeholder:text-muted-foreground dark:placeholder:text-muted-foreground focus:border-indigo-500/30 focus:ring-blue-500"
                  />
                  <p className="text-xs text-muted-foreground dark:text-muted-foreground">
                    {t.deviceManager.nicknameHelp}
                  </p>
                </div>
              </div>
              
              {/* Footer */}
              <div className="flex gap-3 p-6 border-t border-border dark:border-border">
                <Pill
                  variant="secondary"
                  onClick={handleSkipRegistration}
                  className="flex-1"
                >
                  {t.deviceManager.skip}
                </Pill>
                <Pill
                  variant="primary"
                  onClick={handleConfirmRegistration}
                  className="flex-1"
                >
                  <Plus className="w-4 h-4 mr-2" strokeWidth={1.5} />
                  {t.deviceManager.registerAction}
                </Pill>
              </div>
            </div>
          </div>
        )}

        {/* Device Rename Modal - Floating over Device Manager */}
        {renameModalOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center">
            {/* Backdrop with blur */}
            <div 
              className="absolute inset-0 bg-background/20 backdrop-blur-sm"
              onClick={handleCancelRename}
            />
            
            {/* Modal Content */}
            <div className="relative bg-card dark:bg-background rounded-lg shadow-xl border border-border dark:border-border w-full max-w-md max-w-[95vw] md:max-w-md mx-2 md:mx-4">
              {/* Header */}
              <div className="flex items-center gap-3 p-6 border-b border-border dark:border-border">
                <div className="w-10 h-10 bg-indigo-500/20 bg-indigo-500/20 rounded-lg flex items-center justify-center">
                  <Edit3 className="w-5 h-5 text-indigo-400 text-indigo-400" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-foreground">
                    {t.deviceManager.deviceNicknameChange}
                  </h3>
                  <p className="text-sm text-muted-foreground dark:text-muted-foreground">
                    {t.deviceManager.deviceNicknameChangeDesc}
                  </p>
                </div>
              </div>
              
              {/* Body */}
              <div className="p-6 space-y-4">
                <div className="text-center">
                  <p className="text-foreground dark:text-foreground mb-4">
                    <span className="font-medium text-indigo-400 text-indigo-400">
                      {deviceToRename?.name}
                    </span>{t.deviceManager.renameConfirmText}
                  </p>
                </div>
                
                {/* Nickname Input */}
                <div className="space-y-2">
                  <label htmlFor="rename-device-nickname" className="text-sm font-medium text-foreground dark:text-foreground">
                    {t.deviceManager.nicknameMaxLength}
                  </label>
                  <Input
                    id="rename-device-nickname"
                    type="text"
                    value={newDeviceNickname}
                    onChange={(e) => setNewDeviceNickname(e.target.value)}
                    placeholder={t.deviceManager.nicknamePlaceholder}
                    maxLength={20}
                    className="w-full bg-card dark:bg-card text-white text-foreground border-border dark:border-border placeholder:text-muted-foreground dark:placeholder:text-muted-foreground focus:border-indigo-500/30 focus:ring-blue-500"
                  />
                  <div className="flex justify-between items-center">
                    <p className="text-xs text-muted-foreground dark:text-muted-foreground">
                      {t.deviceManager.nicknameEmptyHelp}
                    </p>
                    <p className="text-xs text-muted-foreground dark:text-muted-foreground">
                      {newDeviceNickname.length}/20
                    </p>
                  </div>
                </div>
              </div>
              
              {/* Footer */}
              <div className="flex gap-3 p-6 border-t border-border dark:border-border">
                <Pill
                  variant="secondary"
                  onClick={handleCancelRename}
                  className="flex-1"
                >
                  {t.deviceManager.cancelAction}
                </Pill>
                <Pill
                  variant="primary"
                  onClick={handleConfirmRename}
                  className="flex-1"
                >
                  <Edit3 className="w-4 h-4 mr-2" strokeWidth={1.5} />
                  {t.deviceManager.saveAction}
                </Pill>
              </div>
            </div>
          </div>
        )}

        {/* Device Storage Settings Modal */}
        <DeviceStorageSettingsModal
          open={storageSettingsModalOpen}
          onOpenChange={setStorageSettingsModalOpen}
          deviceId={deviceToConfigureStorage?.id || ''}
          deviceName={deviceToConfigureStorage?.nickname || deviceToConfigureStorage?.name || ''}
        />
    </PageShell>
  )
}